import { useMemo } from 'react'

import type {
  ShipmentListItem,
  ShipmentStatus,
} from '#/shared/api/generated/shipment/models'
import { useListShipmentsApiV1ShipmentsGet } from '#/shared/api/generated/shipment/theCargoShipment'
import {
  isWindowLoading,
  planWindowFetch,
  sliceWindow,
  useTablePagination,
  useTableRowSelection,
  useTableSearchParams,
  visibleSubRange,
} from '#/shared/lib/table'
import type { DataTablePagination } from '#/shared/ui/data-table'
import type { ShipmentStage } from '../model/stages'
import type { ShipmentRow } from '../model/shipment-row'
import { shipmentToRow } from './to-row'

export interface UseShipmentListPageResult<TRow = ShipmentRow> {
  rows: TRow[]
  totalItems: number
  totalTariff: number | undefined
  totalDeposit: number | undefined
  isLoading: boolean
  /** True while any list request is in flight, including background refetches
   *  after a filter change — drives the panel's "Updating…" hint. */
  isFetching: boolean
  isFiltered: boolean
  selectedCount: number
  selectedIds: string[]
  resetSelection: () => void
  rangeFrom: number
  rangeTo: number
  rangeDisabled: boolean
  pagination: DataTablePagination
  onSelectionChange: (ids: string[]) => void
  onRangeChange: (from: number, to: number) => void
}

/**
 * Subset of the `/api/v1/shipments` list filters that the shipment-backed
 * list pages can forward through `useShipmentListPage`. Keys use camelCase
 * and get mapped onto the API's snake_case gte/lte params inside the hook,
 * so callers don't have to reach into the generated param type.
 */
export interface ShipmentListFilters {
  firstAvailableDateGte?: string | null
  firstAvailableDateLte?: string | null
  lastEditedGte?: string | null
  lastEditedLte?: string | null
  taskDeadlineGte?: string | null
  taskDeadlineLte?: string | null
  leadDateGte?: string | null
  leadDateLte?: string | null
  quoteDateGte?: string | null
  quoteDateLte?: string | null
  orderDateGte?: string | null
  orderDateLte?: string | null
  convertedGte?: string | null
  convertedLte?: string | null
  dispatchedGte?: string | null
  dispatchedLte?: string | null
  pickedUpGte?: string | null
  pickedUpLte?: string | null
  deliveredGte?: string | null
  deliveredLte?: string | null
  userId?: string | null
  status?: ShipmentStatus | null
  /**
   * Multi-value filters, pre-joined into the comma-separated form the API
   * expects (`?pickup_states=CA,TX`). `providerIdIn` is the Sources filter.
   */
  timezones?: string | null
  pickupStates?: string | null
  deliveryStates?: string | null
  providerIdIn?: string | null
}

// Status is driven by the header tabs rather than the filter popover, so it
// must not contribute to the "is filtered" signal used by the empty state.
const FILTER_KEYS_IGNORED_FOR_IS_FILTERED = new Set<keyof ShipmentListFilters>([
  'status',
])

function hasActiveFilter(filters: ShipmentListFilters | undefined): boolean {
  if (!filters) return false
  for (const [key, value] of Object.entries(filters) as Array<
    [keyof ShipmentListFilters, ShipmentListFilters[keyof ShipmentListFilters]]
  >) {
    if (FILTER_KEYS_IGNORED_FOR_IS_FILTERED.has(key)) continue
    if (value != null && value !== '') return true
  }
  return false
}

// Maps each camelCase `ShipmentListFilters` key onto its snake_case API
// counterpart. Looping over this table keeps the builder's complexity
// well under the eslint limit (inlining the 6 `?? null` spreads pushes
// `useShipmentListPage` past the 10-branch ceiling).
const SHIPMENT_API_KEY_BY_FILTER: Record<keyof ShipmentListFilters, string> = {
  firstAvailableDateGte: 'first_available_date__gte',
  firstAvailableDateLte: 'first_available_date__lte',
  lastEditedGte: 'dates__last_time_edited__gte',
  lastEditedLte: 'dates__last_time_edited__lte',
  taskDeadlineGte: 'task_deadline__gte',
  taskDeadlineLte: 'task_deadline__lte',
  leadDateGte: 'dates__lead__gte',
  leadDateLte: 'dates__lead__lte',
  quoteDateGte: 'dates__quote__gte',
  quoteDateLte: 'dates__quote__lte',
  orderDateGte: 'dates__order__gte',
  orderDateLte: 'dates__order__lte',
  convertedGte: 'dates__converted__gte',
  convertedLte: 'dates__converted__lte',
  dispatchedGte: 'dates__dispatched__gte',
  dispatchedLte: 'dates__dispatched__lte',
  pickedUpGte: 'dates__picked_up__gte',
  pickedUpLte: 'dates__picked_up__lte',
  deliveredGte: 'dates__delivered__gte',
  deliveredLte: 'dates__delivered__lte',
  userId: 'user_id',
  status: 'status',
  timezones: 'timezones',
  pickupStates: 'pickup_states',
  deliveryStates: 'delivery_states',
  providerIdIn: 'provider_id__in',
}

function toShipmentListApiParams(
  filters: ShipmentListFilters | undefined,
): Record<string, string | null> {
  const source = filters ?? {}
  const out: Record<string, string | null> = {}
  for (const key of Object.keys(SHIPMENT_API_KEY_BY_FILTER) as Array<
    keyof ShipmentListFilters
  >) {
    out[SHIPMENT_API_KEY_BY_FILTER[key]] = source[key] ?? null
  }
  return out
}

type PageTotals = {
  total: number
  total_tariff?: number
  total_deposit?: number
}

function pickTotals(page: PageTotals | undefined) {
  return {
    items: page?.total ?? 0,
    tariff: page?.total_tariff,
    deposit: page?.total_deposit,
  }
}

// The row-selection scope: whenever any of these URL params change the visible
// result set is different, so the selection must reset. Kept out of the hook
// body so the optional-chaining branch doesn't count against its complexity.
function selectionScopeKey(
  stage: ShipmentStage,
  filters: ShipmentListFilters | undefined,
  page: number,
  from: number,
  to: number,
): string {
  // The stage prefix makes the key unique across lists: a workspace tab that
  // navigates leads → orders must not restore one list's ids into the other.
  return `${stage}|${filters?.status ?? ''}|${page}|${from}|${to}`
}

/**
 * Shared plumbing for every shipment-backed list page (leads, quotes,
 * orders). Reads the URL range + search, fetches the matching stage from
 * the list-shipments endpoint, slices the response to exactly the user's
 * visible range, and hands back the props each page needs for its
 * toolbar's DataDropdown and the DataTable below it.
 *
 * Pages with extra row-level data (orders carries pickup/delivery dates,
 * trucking metadata, etc.) can pass a custom `map` that builds their own
 * row type on top of — or instead of — the default `ShipmentRow`.
 */
export function useShipmentListPage<TRow = ShipmentRow>(
  stage: ShipmentStage,
  map: (item: ShipmentListItem) => TRow = shipmentToRow as (
    item: ShipmentListItem,
  ) => TRow,
  filters?: ShipmentListFilters,
  options?: { enabled?: boolean },
): UseShipmentListPageResult<TRow> {
  const {
    from,
    to,
    size,
    page,
    search: committedSearch,
  } = useTableSearchParams()
  // Selection is scoped to the view — list, status (tab), page, date-range
  // window — and snapshotted per workspace tab, so it clears when the result
  // set changes but survives a workspace-tab switch back to the same view.
  const selection = useTableRowSelection(
    selectionScopeKey(stage, filters, page, from, to),
  )

  // Only the visible sub-page is fetched — at most `size` (<=100) rows, which
  // spans one or two max-size API pages.
  const sub = visibleSubRange(from, to, size, page)
  const plan = useMemo(
    () => planWindowFetch(sub.from, sub.to, size),
    [sub.from, sub.to, size],
  )
  const enabled = options?.enabled ?? true

  const baseParams = {
    stage,
    q: committedSearch.length > 0 ? committedSearch : null,
    ...toShipmentListApiParams(filters),
  }

  // No `keepPreviousData`: a cached tab/page renders instantly, and a tab whose
  // data isn't loaded yet shows the skeleton while it loads — instead of
  // flashing the previous tab's rows. `isPending` is the exact "loading with no
  // cached data" signal the table's skeleton wants.
  const query = useListShipmentsApiV1ShipmentsGet(
    { ...baseParams, page: plan.page1, size: plan.size },
    { query: { enabled } },
  )
  // Second page only when the sub-range straddles two API pages.
  const secondQuery = useListShipmentsApiV1ShipmentsGet(
    { ...baseParams, page: plan.page2, size: plan.size },
    { query: { enabled: enabled && plan.twoPages } },
  )

  const pageData = query.data?.data
  const totals = pickTotals(pageData)
  const { pagination, rangeFrom, rangeTo, rangeDisabled, onRangeChange } =
    useTablePagination(totals.items)

  const secondItems = secondQuery.data?.data.items
  const rows = useMemo(
    () => sliceWindow(pageData?.items, secondItems, plan).map(map),
    [pageData, secondItems, plan, map],
  )

  return {
    rows,
    totalItems: totals.items,
    totalTariff: totals.tariff,
    totalDeposit: totals.deposit,
    isLoading: isWindowLoading(query.isPending, secondQuery.isPending, plan),
    isFetching: query.isFetching || secondQuery.isFetching,
    isFiltered: committedSearch.length > 0 || hasActiveFilter(filters),
    selectedCount: selection.selectedCount,
    selectedIds: selection.selectedIds,
    resetSelection: selection.resetSelection,
    rangeFrom,
    rangeTo,
    rangeDisabled,
    pagination,
    onSelectionChange: selection.onSelectionChange,
    onRangeChange,
  }
}
